import { z } from 'zod';
import {
  UPPERCASE_REGEX,
  SPECIAL_CHAR_REGEX,
  DIGITS_ONLY_REGEX,
  LETTERS_ONLY_REGEX,
  LETTERS_ONLY_OPTIONAL_REGEX,
} from '@unreserved/configs';
import type { ValidationRule } from '@unreserved/types';

// ============================================================================
// Password Validation Rules
// ============================================================================

/**
 * Password validation rules for UI display
 * These rules are used to show real-time validation feedback to users
 */
export const passwordRules: ValidationRule[] = [
  {
    id: 'length',
    label: 'Must be at least 8 characters',
    validate: (password: string) =>
      password.length >= 8 && password.length <= 100,
  },
  {
    id: 'uppercase',
    label: 'Must be having 1 uppercase',
    validate: (password: string) => UPPERCASE_REGEX.test(password),
  },
  {
    id: 'special',
    label: 'Must be having 1 special character',
    validate: (password: string) => SPECIAL_CHAR_REGEX.test(password),
  },
  {
    id: 'valid-chars',
    label: 'Only alphanumeric and @$!%*?& characters allowed',
    validate: (password: string) => /^[A-Za-z\d@$!%*?&]*$/.test(password),
  },
];

// ============================================================================
// Common Field Schemas
// ============================================================================

/**
 * Reusable email field schema
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(255, 'Email is too long');

/**
 * Reusable password field schema with validation rules
 */
export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(8, 'Must be at least 8 characters')
  .max(100, 'Password is too long')
  .regex(UPPERCASE_REGEX, 'Must be having 1 uppercase')
  .regex(SPECIAL_CHAR_REGEX, 'Must be having 1 special character')
  .regex(
    /^[A-Za-z\d@$!%*?&]+$/,
    'Password contains invalid characters. Only alphanumeric and @$!%*?& are allowed',
  );

/**
 * Simple password field for login (less strict validation)
 */
export const passwordLoginSchema = z
  .string({
    required_error: 'Password is required',
  })
  .min(1, 'Password is required')
  .min(8, 'Password must be at least 8 characters')
  .max(100, 'Password is too long');

// ============================================================================
// Login Schema
// ============================================================================

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export type LoginFormData = z.infer<typeof loginSchema>;

// ============================================================================
// Forgot Password Schema
// ============================================================================

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

// ============================================================================
// Reset Password Schema
// ============================================================================

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

// ============================================================================
// Signup Schema
// ============================================================================

export const signupSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name is too long')
      .regex(LETTERS_ONLY_REGEX, 'First name must contain only letters'),
    lastName: z
      .string()
      .max(50, 'Last name is too long')
      .regex(LETTERS_ONLY_OPTIONAL_REGEX, 'Last name must contain only letters')
      .optional()
      .or(z.literal('')),
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    countryCode: z.string().min(1, 'Country code is required'),
    contactNumber: z
      .string()
      .min(1, 'Contact number is required')
      .regex(DIGITS_ONLY_REGEX, 'Contact number must contain only digits')
      .min(10, 'Contact number must be at least 10 digits')
      .max(10, 'Contact number must be exactly 10 digits'),
    profileImage: z.string().optional(),
    propertyAddress: z
      .string()
      .min(1, 'Property address is required')
      .max(105, 'Property address is too long'),
    interests: z.array(z.string()).optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SignupFormData = z.infer<typeof signupSchema>;
